/* eslint-disable @typescript-eslint/no-unused-vars */
import { BeginPolling, EndPolling } from '../polling/polling.actions';
import { inject, Injectable } from '@angular/core';
import { Action, Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { from, Observable, of } from 'rxjs';
import { find, isEmpty, pick } from 'lodash/fp';
import {
  ApiPolicyReceived,
  ApplicationBatchError,
  ExtractImages,
  FailedToScan,
  FetchApiPolicy,
  FetchApiPolicyError,
  FetchApiStatusError,
  FetchApiStatusSuccess,
  GetScansError,
  GetScansSuccess,
  ImagesAcquired,
  ImagesBatchSent,
  ImageSent,
  ImagesExtracted,
  IPayloadAction,
  NothingToScan,
  ScanEntitiesReceived,
  ScannerActions,
  ScannerDestroyed,
  ScannerInitialized,
  ScannerListReceived,
  ScannerLoaded,
  ScannerSelected,
  SelectDefaultScanner,
  SelectScanner,
  SendImagesBatch,
  SendImagesBatchError,
  SubmitToScoringCompleted,
  SubmitToScoringError,
  UpdateScanError,
} from './scanner.actions';
import { ScannerAPI } from './scanner.api';
import { LocalScanningWebApiService } from '../../services/local-scanning-web-api.service';
import { DefaultScannerService, IScannerSource } from '../../services/default-scanner.service';
import { catchError, filter, pluck, switchMap, withLatestFrom } from 'rxjs/operators';
import { UpdatePending } from '../pending/pending.actions';
import { ScriptLoaderService, setLocalStorageItem } from '@drc-eca/eca-lrc-common-ui';
import { environment } from '../../../environments/environment';

@Injectable()
export class ScannerEffects {
  private actions$ = inject(Actions);
  private store$ = inject<Store<any>>(Store);
  private webapi = inject(LocalScanningWebApiService);
  private scriptLoader = inject(ScriptLoaderService);


  // Do not use this externally, it's only public for unit tests
  public scanner: ScannerAPI;
  loadScanner$: Observable<Action> = createEffect(() => this.actions$
    .pipe(
      ofType(ScannerActions.LOAD_SCANNER),
      filter(() => {
        // checking to see if we have already loaded the scanner scripts here, dispatching ScannerLoaded to simulate if its already happened
        if (!this.scanner) {
          return true;
        }
        this.store$.dispatch(new ScannerLoaded());
        return false;
      }),
      switchMap((action: IPayloadAction) => {
        const initiatejs = `${action.payload}/dynamsoft.webtwain.initiate.js`;
        const configjs = `${action.payload}/dynamsoft.webtwain.config.js`;
        console.log('Loading Dynamsoft config. files.');
        return this.scriptLoader.load(initiatejs, {type: 'text'}).pipe(
          switchMap(() => {
            return this.scriptLoader.load(configjs, {type: 'text'});
          })
        );
      }),
      switchMap(() => {
        console.log('Loaded Dynamsoft config. files. Loading Dynamsoft.');
        this.getDWT().Load();
        return of(true);
      }),
      switchMap(() => {
        console.log(`Loaded ${this.getDWT().ProductName} 'initiate'.`);
        return from(new Promise((resolve, reject) => {
          this.scanner = new ScannerAPI(this.getDynamsoft(), () => {
            console.log(`INFO: ${this.getDWT().ProductName} is Ready`);
            resolve(true);
          });
        }));
      }),
      switchMap(() => {
        return of(new ScannerLoaded());
      })
    ));
  initializeScanner$: Observable<Action> = createEffect(() => this.actions$
    .pipe(
      ofType(ScannerActions.INITIALIZE_SCANNER),
      switchMap((action: IPayloadAction) => {
        return from(this.scanner.initializeScanner());
      }),
      switchMap(() => {
        return of(new ScannerInitialized());
      }),
    ));
  scannerInitialized$: Observable<Action> = createEffect(() => this.actions$
    .pipe(
      ofType(ScannerActions.SCANNER_INITIALIZED),
      switchMap(() => from(this.scanner.getSourceList())),
      switchMap((scannerMap: IScannerSource[]) => {
        return this.selectDefaultScanner(scannerMap);
      }),
    ));
  selectDefaultScanner$: Observable<Action> = createEffect(() => this.actions$
    .pipe(
      ofType(ScannerActions.SELECT_DEFAULT_SCANNER),
      switchMap(() => from(this.scanner.getSourceList())),
      switchMap((scannerMap: IScannerSource[]) => {
        return this.selectDefaultScanner(scannerMap);
      }),
    ));
  getScannerList$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(ScannerActions.GET_SCANNER_LIST),
    switchMap(() => {
      return from(this.scanner.getSourceList());
    }),
    switchMap((sources) => {
      return of(new ScannerListReceived(sources));
    }),
  ));
  selectScanner$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(ScannerActions.SELECT_SCANNER),
    switchMap((action: IPayloadAction) => {
      setLocalStorageItem('localScanning:defaultScanner', this.formatScannerIdentifier(action.payload));
      this.scanner.selectSourceByIndex(action.payload.index, action.payload.ImageCaptureDriverType);
      return of(new ScannerSelected(action.payload));
    }),
  ));
  acquireImages$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(ScannerActions.ACQUIRE_IMAGES),
    switchMap(() => {
      if (!this.scanner.checkScannerSource()) {
        return of({noScannerSelected: true});
      }

      return from(this.scanner.acquireImages())
        .pipe(
          // catch here; a catch on the main observable chain
          // would "complete" the observable and unsubscribe the effect
          catchError((e: Error) => {
            console.log(`Error message in acquireImages:`, e.message);

            const feederEmptyErrorRegexes = [
              /no documents in feeder/i,
              /feeder is empty/i,
            ];

            for (const errorRegex of feederEmptyErrorRegexes) {
              if (e.message?.match(new RegExp(errorRegex))) {
                return of({feederEmpty: true});
              }
            }

            console.log(`Error in acquireImages:`, e);
            return of({failedToScan: true, error: e});
          })
        );
    }),
    switchMap((result) => {
      if (result?.noScannerSelected) {
        return of(new SelectDefaultScanner());
      }

      if (result?.failedToScan) {
        return of(new FailedToScan(result.error));
      }

      if (result?.feederEmpty) {
        if (!this.scanner.checkScannerSource()) {
          // no source selected go for default
          return of(new NothingToScan(), new SelectDefaultScanner());
        }

        return of(new NothingToScan());
      }

      return of(new ImagesAcquired());
    })
  ));
  imagesAcquired$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(ScannerActions.IMAGES_ACQUIRED),
    switchMap(() => {
      return of(new ExtractImages());
    }),
  ));
  extractImages$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(ScannerActions.EXTRACT_IMAGES),
    switchMap(() => {
      return from(this.scanner.extractAndClearImages());
    }),
    withLatestFrom(
      this.store$.select('session', 'id'),
      this.store$.select('siteInformation', 'client'),
    ),
    switchMap(([blobs, sessionId, client]) => {
      if (isEmpty(blobs)) {
        return of(new NothingToScan());
      }
      return of(new ImagesExtracted({
        blobs,
        sessionId,
        client,
      }));

    }),
  ));
  imagesExtracted$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(ScannerActions.IMAGES_EXTRACTED),
    switchMap((action: IPayloadAction) => {
      if (isEmpty(action.payload.blobs)) {
        const message =
          'There was an application error while extracting images.\n' +
          'The ngrx Effect for IMAGES EXTRACTED was used incorrectly. It should have received images in payload, but got none.';
        return of(new ApplicationBatchError(message));
      }
      return of(new FetchApiPolicy());
    }),
  ));
  fetchApiPolicy$: Observable<Action> = createEffect(() => this.actions$
    .pipe(
      ofType(ScannerActions.FETCH_API_POLICY),
      switchMap((action: FetchApiPolicy) => {
        /* istanbul ignore next */
        return this.webapi.getScanMetadataPolicy()
          .pipe(
            catchError(e => {
              console.log('ERROR: getScanMetadataPolicy failed', e);
              // catch here; a catch on the main observable chain
              // would "complete" the observable and unsubscribe the effect
              return of(new FetchApiPolicyError(e));
            })
          );
      }),
      switchMap((result: any) => {
        /* istanbul ignore next */
        if (result.type === ScannerActions.FETCH_API_POLICY_ERROR) {
          console.log('FETCH_API_POLICY_ERROR');
          return of(result);
        }
        return of(new ApiPolicyReceived(result));
      }),
    ));
  apiPolicyReceived$: Observable<Action> = createEffect(() => this.actions$
    .pipe(
      ofType(ScannerActions.API_POLICY_RECEIVED),
      switchMap((apiPolicyAction) => {
        return of(new SendImagesBatch(), new BeginPolling());
      }),
    ));
  sendImagesBatch$: Observable<Action> = createEffect(() => this.actions$
    .pipe(
      ofType(ScannerActions.SEND_IMAGES_BATCH),
      withLatestFrom(this.store$.select((state) => {
        return {
          ...state.scans,
          sessionId: state.session.id,
        };
      })),
      switchMap(([action, scans]) => {
        console.log(`Uploading images for session ${scans.sessionId}`);
        const uploads = pick(scans.unsynced, scans.entities);
        return this.webapi.sendScans(uploads)
          .pipe(
            catchError(e => {
              // catch here; a catch on the main observable chain
              // would "complete" the observable and unsubscribe the effect
              return of(new SendImagesBatchError(e));
            })
          );
      }),
      switchMap((result: any) => {
        if (result.type === ScannerActions.SEND_IMAGES_BATCH_ERROR) {
          return of(result);
        }
        return of(new ImageSent(result));

      }),
    ));
  imageSent$: Observable<any> = createEffect(() => this.actions$
    .pipe(
      ofType(ScannerActions.IMAGE_SENT),
      withLatestFrom(this.store$.select((state) => state.scans)),
      filter(([action, scans]) => isEmpty(scans.unsynced)),
      switchMap(([action, scans]) => {
        return of(new ImagesBatchSent());
      })
    ));
  destroyScanner$: Observable<Action> = createEffect(() => this.actions$
    .pipe(
      ofType(ScannerActions.DESTROY_SCANNER),
      switchMap((action: IPayloadAction) => {
        this.scanner.destroyScanner();
        return of(new ScannerDestroyed());
      }),
    ));
  updateScanStatus$: Observable<Action> = createEffect(() => this.actions$
    .pipe(
      ofType(ScannerActions.UPDATE_SCAN_STATUS),
      withLatestFrom(this.store$.select('session').pipe(pluck('id'))),
      switchMap(([action, sessionId]) => {
        return this.webapi.getScansBySessionId(sessionId)
          .pipe(
            catchError(e => {
              // catch here; a catch on the main observable chain
              // would "complete" the observable and unsubscribe the effect
              return of(new UpdateScanError(e));
            })
          );
      }),
      switchMap((result): Observable<any> => {
        if (result['type'] && result['type'] === ScannerActions.UPDATE_SCAN_ERROR) {
          return of(result);
        }
        return of(new ScanEntitiesReceived(result));

      }),
    ));
  scanEntitiesReceived$: Observable<Action> = createEffect(() => this.actions$
    .pipe(
      ofType(ScannerActions.SCANS_ENTITIES_RECEIVED),
      withLatestFrom(this.store$.select('scans').pipe(pluck('completed'))),
      filter(([action, isCompleted]: [Action, boolean]) => isCompleted),
      switchMap(() => {
        return of(new EndPolling(), new UpdatePending());
      }),
    ));
  submitToScoring$: Observable<Action> = createEffect(() => this.actions$
    .pipe(
      ofType(ScannerActions.SUBMIT_TO_SCORING),
      switchMap((action: IPayloadAction) => {
        return this.webapi.submitToScoring(action.payload)
          .pipe(
            catchError(e => {
              // catch here; a catch on the main observable chain
              // would "complete" the observable and unsubscribe the effect
              return of(new SubmitToScoringError(e));
            })
          );
      }),
      switchMap((result: any) => {
        if (result.type === ScannerActions.SUBMIT_TO_SCORING_ERROR) {
          return of(result);
        }
        return of(new SubmitToScoringCompleted());

      })
    ));
  getScans$: Observable<Action> = createEffect(() => this.actions$
    .pipe(
      ofType(ScannerActions.GET_SCANS),
      switchMap((action: IPayloadAction) => {
        return this.webapi.getScansBySessionId(action.payload.sessionId, action.payload.populateAllFields)
          .pipe(
            catchError(e => {
              // catch here; a catch on the main observable chain
              // would "complete" the observable and unsubscribe the effect
              return of(new GetScansError(e));
            })
          );
      }),
      switchMap((result: any) => {
        if (result.type === ScannerActions.GET_SCANS_ERROR) {
          return of(result);
        }
        return of(new GetScansSuccess(result));

      })
    ));
  getApiStatus$: Observable<Action> = createEffect(() => this.actions$
    .pipe(
      ofType(ScannerActions.FETCH_API_STATUS),
      switchMap((action: IPayloadAction) => {
        return this.webapi.getApiStatus()
          .pipe(
            catchError(e => {
              console.log('Cannot retrieve server status. ' + JSON.stringify(e.message));
              return of(new FetchApiStatusError());
            })
          );
      }),
      switchMap((result: any) => {
        if (result.type === ScannerActions.FETCH_API_STATUS_ERROR) {
          return of(result);
        }

        const serviceName = result.buildServiceName ? 'Local Scanning Service' : 'Form Recognition Service';

        console.log('Backend API URL:', environment.localScanningApiUrl);
        console.log(`INFO: ${serviceName} build info:`, JSON.stringify(result));

        return of(new FetchApiStatusSuccess());
      })
    ));

  getDynamsoft() {
    return window['Dynamsoft'];
  }

  getDWT() {
    if (!this.getDynamsoft()?.DWT) {
      console.log('ERROR Dynamsoft WebTwain object undefined.');
    }
    return this.getDynamsoft().DWT;
  }

  formatScannerIdentifier(source: IScannerSource) {
    const scannerIdentifier = [
      source.index,
      source.name,
    ];

    /* eslint-disable-next-line */
    if (source.ImageCaptureDriverType != null) {
      scannerIdentifier.push(source.ImageCaptureDriverType);
    }

    return scannerIdentifier.join(':');
  }

  selectDefaultScanner(scannerMap: IScannerSource[]): Observable<any> {
    const defaultScanner: IScannerSource = new DefaultScannerService().getDefaultScanner();
    if (defaultScanner) {
      // the scanner index and name might not match if any new scanners have been installed
      const match = find(defaultScanner, scannerMap);

      if (match) {
        return of(new SelectScanner(match));
      }

      const scannerIdentifier = this.formatScannerIdentifier(defaultScanner);
      const mapMsg = scannerMap.map((x) => this.formatScannerIdentifier(x)).join(', ');
      console.warn(`WARNING: Scanner '${scannerIdentifier}' did not match any of ${mapMsg}`);
    }

    // if no default was set OR the default was invalid, dispatch an unused action
    return of({type: 'NO_DEFAULT_SCANNER'});
  }

}
